import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormGroup,
  UntypedFormArray,
  UntypedFormGroup,
} from '@angular/forms';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { ProductService } from 'src/app/core/api/product/product.service';
import { DialogService } from 'src/app/core/services/dialog/dialog.service';
import { RegisterService } from 'src/app/core/services/register/register.service';
import { TranslationService } from 'src/app/core/services/translation/translation.service';
import { BusinessDataFormObject } from 'src/app/shared/models/business-data/business-data-form-object/business-data-form-object';
import { BusinessEquipmentFormObject } from 'src/app/shared/models/business-equipment/business-equipment-form-object/business-equipment-form-object';
import { BusinessRepresentativeFormObject } from 'src/app/shared/models/business-representative/business-representative-form-object/business-representative-form-object';
import { fromTRegisterEquipmentFormGroup } from 'src/app/shared/models/dtos/requests/equipment-sap-registrarion-request-dto/equipment-sap-registration-request-dto';
import { errorObjectBuilder } from 'src/app/shared/validators/registration-validators';
import { RoutesKeys } from 'src/app/utils/routes';
import {
  BusinessEquipmentFormComponent,
  TBusinessEquipmentFormPhase,
} from '../../molecules/business-equipment-form/business-equipment-form.component';
import { SnackBarService } from 'src/app/core/services/snackbar/snackbar.service';
import { CountryList } from 'src/app/shared/models/dtos/register-form/country-list/country-list';
import { RecaptchaComponent } from '../../molecules/recaptcha/recaptcha.component';
import { environment } from '../../../../../environments/environment';

@Component({
  selector: 'app-carousel-register',
  templateUrl: './carousel-register.component.html',
  styleUrls: ['./carousel-register.component.scss'],
})
/**
 * Carousel organism for register that displays pages one at a time
 */
export class CarouselRegisterComponent implements OnInit, OnDestroy {
  @Input() pages!: string[];
  @Input() finishLabel: string = '';
  @Input() previousPageLabel: string = '';
  @Input() firstPageLabel: string = '';
  @Input() secondPageLabel: string = '';
  @Input() thirdPageLabel: string = '';
  @Input() businessData!: BusinessDataFormObject;
  @Input() equipmentData!: BusinessEquipmentFormObject;
  @Input() businessRepresentative!: BusinessRepresentativeFormObject;
  @Input() termsFormGroup!: UntypedFormGroup;
  @Input() creatingAccount = false;
  @Input() errorMessage = '';
  @Output() finish = new EventEmitter<any>();
  @Input() migrating = false;
  @Input() canEditEquipments = true;
  @Input() recaptchaFormGroup!: FormGroup;
  @ViewChild('captchaRef') captchaRef?: RecaptchaComponent;
  @ViewChild(BusinessEquipmentFormComponent)
  // eslint-disable-next-line indent
  businessEquipmentForm?: BusinessEquipmentFormComponent;
  activePage: 1 | 2 | 3 = 1;
  currentPageComponent: string = 'page-1';
  RoutesKeys = RoutesKeys;
  token: string | undefined;
  businessEquipmentFormPhase: TBusinessEquipmentFormPhase = 'LNR_REGISTER';
  nextPageLoading = false;
  readonly labels = {
    couldNotCheckPrinterSerialData: 'unable-check-printer-serial-number',
    couldNotRegisterPrinter: 'unable-register-printer',
    noCommPrinterDatabase: 'could-not-communicate-with-printer-database',
    ok: 'ok',
  };
  private readonly subsink = new Subscription();
  isAddingEquipment = true;

  equipmentRegisterFeedback?: 'eligible' | 'registered';
  recaptchaEnabled = environment.recaptcha.enabled;

  /**
   * @constructor
   */
  constructor(
    public readonly registerService: RegisterService,
    private readonly router: Router,
    private readonly dialogService: DialogService,
    private readonly translationService: TranslationService,
    private readonly productService: ProductService,
    private snackbarService: SnackBarService
  ) {
    this.token = undefined;
  }

  /**
   * pipes up active page
   */
  ngOnInit() {
    this.activePage = this.registerService.activeStep;
    this.setComponent();
    this.subsink.add(this.translationService.translateObject$(this.labels));
    this.registerService.disableCountryField = false;
  }

  /**
   * Clean up resources
   */
  ngOnDestroy() {
    this.subsink.unsubscribe();
    this.registerService.resetBusinessEquipmentFormGroup();
    this.registerService.resetRegisterEquipmentFormGroup();
    this.registerService.activeStep = 1;
    this.equipmentData.formGroup.enable();
  }

  /**
   * @return {number} number of pages this carousel possesses
   */
  numberOfPages(): number {
    return this.pages.length;
  }

  /**
   * Sets next page as the active page
   */
  async nextPage() {
    if (this.activePage === 1) {
      this.nextPageOnPageOne();
    } else {
      this.goToNextPage();
    }
  }

  /**
   * @param { FormArray } equipmentsArray
   * @param { AbtractControl } serial
   * @return {boolean} whether or not validation api should be called
   */
  shouldCallValidationAPI(
    equipmentsArray: FormArray,
    serial: AbstractControl
  ): boolean {
    if (
      equipmentsArray.controls.reduce(
        (acc, control) =>
          acc && (control.disabled || control.get('id')?.value != undefined),
        true
      )
    ) {
      this.goToNextPage();
      return false;
    } else {
      const hasDuplicate = equipmentsArray.controls
        .slice(0, -1)
        .reduce(
          (acc, control) =>
            acc || control.get('serial')?.value === serial.value,
          false
        );
      if (hasDuplicate) {
        serial.setErrors(errorObjectBuilder('duplicate-serial'));
        return false;
      }
    }
    return true;
  }

  /**
   * When next page is clicked on lnr register phase for
   * business equipment form, a validation API may be called
   * or perhaps truly going to the next page will be the outcome
   * @param { FormArray } equipmentsArray
   * @param { AbtractControl } serial
   * @param { AbstractControl } equipment
   * @param { CountryList } country
   */
  lnrRegisterNextPageClick(
    equipmentsArray: FormArray,
    serial: AbstractControl,
    equipment: AbstractControl,
    country?: CountryList
  ) {
    if (!this.shouldCallValidationAPI(equipmentsArray, serial)) {
      return;
    }
    this.nextPageLoading = true;
    equipment.disable();
    this.registerService.disableCountryField = true;
    this.registerService.validateSerial(
      serial,
      equipment,
      country!.initials,
      false,
      undefined,
      this.nextPhase.bind(this)
    );
  }

  /**
   * Continue the equipment register process
   */
  async nextPhase() {
    this.businessEquipmentFormPhase =
      this.registerService.businessEquipmentFormPhase;
    this.equipmentRegisterFeedback =
      this.registerService.equipmentRegisterFeedback;
    this.nextPageLoading = false;
    this.isAddingEquipment = false;
  }

  /**
   * Triggers epson register api
   * @param {AbstractControl} serial
   */
  epsonRegister(serial: AbstractControl) {
    this.registerService.registerEquipmentFormGroup.disable();
    this.nextPageLoading = true;
    const country = this.registerService.findCountryById(
      this.registerService.registerEquipmentFormGroup.controls.country!
        .value as number
    );
    this.productService
      .epsonSAPEquipmentRegistration(
        fromTRegisterEquipmentFormGroup(
          this.registerService.registerEquipmentFormGroup,
          serial.value,
          country!.initials
        )
      )
      .subscribe({
        next: (sapResponse) => {
          if (sapResponse.active) {
            this.businessEquipmentFormPhase = 'LNR_REGISTER';
            this.equipmentRegisterFeedback = 'registered';
            if (this.businessEquipmentForm) {
              this.businessEquipmentForm.isAddingEquipment = false;
            }
            this.translationService
              .translateString('success-updating-data')
              .then((string) => this.snackbarService.success(string));
          } else {
            this.dialogService.openDialog({
              title: this.labels.couldNotRegisterPrinter,
              content: this.labels.couldNotRegisterPrinter,
              successButtonText: this.labels.ok,
            });
          }
        },
        error: () => {
          this.dialogService.openDialog({
            title: this.labels.couldNotRegisterPrinter,
            content: this.labels.noCommPrinterDatabase,
            successButtonText: this.labels.ok,
          });
        },
      })
      .add(() => {
        this.nextPageLoading = false;
        this.registerService.registerEquipmentFormGroup.enable();
      });
  }

  /**
   * Next page for step one
   * it may go to the next page or trigger api calls
   */
  nextPageOnPageOne() {
    const equipmentsArray = this.equipmentData.formGroup.controls[
      'equipments'
    ] as UntypedFormArray;
    const equipment = equipmentsArray.at(equipmentsArray.length - 1);
    const country = this.registerService.findCountryById(
      this.registerService.countryEquipmentFormGroup.controls.country!
        .value as number
    );
    const serial: any = equipment.get('serial');
    if (!serial?.value) {
      return;
    }
    if (this.businessEquipmentFormPhase === 'LNR_REGISTER') {
      this.lnrRegisterNextPageClick(
        equipmentsArray,
        serial,
        equipment,
        country
      );
    } else if (
      this.businessEquipmentFormPhase === 'EPSON_UNREGISTERED_PENDING'
    ) {
      this.epsonRegister(serial);
    }
  }

  /**
   * Tries to go to next page
   */
  goToNextPage() {
    if (this.isCurrentFormGroupInvalid()) {
      return;
    }
    const possiblyNext = this.activePage + 1;
    if (possiblyNext <= this.numberOfPages()) {
      this.activePage = possiblyNext as 1 | 2 | 3;
      this.registerService.activeStep = this.activePage;
    }
    this.setComponent();
  }

  /**
   * Sets previous page as the active page
   */
  previousPage(): void {
    const possiblyPrevious = this.activePage - 1;
    if (possiblyPrevious > 0) {
      this.activePage = possiblyPrevious as 1 | 2 | 3;
      this.registerService.activeStep = this.activePage;
    }
    this.setComponent();
  }

  /**
   * Emits finish event
   */
  finishAction() {
    this.finish.emit();
    this.captchaRef?.reset();
  }

  /**
   * Set current component according to its page
   */
  private setComponent(): void {
    this.currentPageComponent = this.pages[this.activePage - 1];
  }

  /**
   * @return {boolean} if a "finish" button should be displayed in last page
   */
  hasFinishButton(): boolean {
    return (
      this.finishLabel?.length > 0 && this.activePage === this.numberOfPages()
    );
  }

  /**
   * Control the validators by page
   * @return {boolean} validator status
   */
  isCurrentFormGroupInvalid(): boolean {
    const page = this.activePage;
    const countryStatus =
      this.registerService.countryEquipmentFormGroup.get('country')?.value;
    let pageStatus: boolean = false;
    switch (page) {
      case 1:
        pageStatus = this.equipmentData.formGroup.invalid || !countryStatus;
        if (this.businessEquipmentFormPhase === 'EPSON_UNREGISTERED_PENDING') {
          this.registerService.registerEquipmentFormGroup
            .get('country')
            ?.setValue(
              this.registerService.countryEquipmentFormGroup.get('country')!
                .value
            );
          pageStatus =
            this.registerService.registerEquipmentFormGroup.invalid ||
            this.registerService.registerEquipmentFormGroup.pending;
        } else if (this.migrating) {
          pageStatus = false;
        }
        break;
      case 2:
        pageStatus =
          this.businessData.formGroup.invalid ||
          this.businessData.formGroup.pending;
        break;
      case 3:
        pageStatus =
          this.isRepresentativeFormInvalid || this.termsFormGroup.invalid;
        break;
    }
    return pageStatus;
  }

  /**
   * True if any of the forms are invalid
   * @return {boolean}
   */
  anyFormInvalid(): boolean {
    return (
      this.businessData.formGroup.invalid ||
      this.equipmentData.formGroup.invalid ||
      this.isRepresentativeFormInvalid ||
      this.termsFormGroup.invalid ||
      (this.recaptchaEnabled && this.recaptchaFormGroup.invalid)
    );
  }

  /**
   * Decides if representative form is invalid or not
   */
  private get isRepresentativeFormInvalid() {
    if (this.migrating) {
      return (
        this.businessRepresentative.formGroup.controls['name'].invalid ||
        this.businessRepresentative.formGroup.controls['lastName'].invalid ||
        this.businessRepresentative.formGroup.controls['email'].invalid
      );
    }
    return this.businessRepresentative.formGroup.invalid;
  }

  /**
   * @return {boolean}
   */
  isFinishDisabled(): boolean {
    return this.anyFormInvalid() || this.creatingAccount;
  }

  /**
   * Route to login page
   */
  backToLogin() {
    if (this.businessEquipmentFormPhase === 'EPSON_UNREGISTERED_PENDING') {
      this.businessEquipmentFormPhase = 'LNR_REGISTER';
      const equipmentsArray = this.equipmentData.formGroup.controls[
        'equipments'
      ] as UntypedFormArray;
      const equipment = equipmentsArray.at(equipmentsArray.length - 1);
      equipment.enable();
      this.registerService.disableCountryField = false;
    } else {
      this.registerService.resetForms();
      this.router.navigate([RoutesKeys.LOGIN]);
    }
  }

  /**
   * Marks controls as dirty or touched to display errors
   */
  disabledFinishClick() {
    if (!this.isFinishDisabled()) {
      return;
    }
    this.termsFormGroup.markAsDirty();
    this.businessRepresentative.formGroup.markAllAsTouched();
  }

  /**
   *
   */
  disabledNextPageClick() {
    if (this.businessEquipmentFormPhase === 'EPSON_UNREGISTERED_PENDING') {
      this.registerService.registerEquipmentFormGroup.controls.agree.markAsDirty();
      this.registerService.registerEquipmentFormGroup.markAllAsTouched();
    }
  }

  /**
   * Next page label decision
   */
  get nextPageLabel() {
    if (this.activePage !== 1) {
      return 'next-step';
    }

    if (this.businessEquipmentFormPhase === 'EPSON_UNREGISTERED_PENDING') {
      return 'register-your-product';
    }

    const equipmentsControl = this.equipmentData?.formGroup?.get('equipments');

    if (equipmentsControl instanceof FormArray && equipmentsControl.controls) {
      const hasEquipmentToValidate = equipmentsControl.controls.some(
        (control) => !control.disabled && control.get('id')?.value == null
      );

      return hasEquipmentToValidate ? 'validate' : 'next-step';
    }

    return 'validate';
  }
}
